import React, { useRef, useState, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { CalendarCheck, RefreshCw, Settings, Target } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPatch } from '../../api/client';
import type { Event } from '../../api/types';
import { Button } from '../../components/ui/Button';
import { EventStatusBadge } from '../../components/ui/Badge';
import { PageHeader } from '../../components/ui/PageHeader';
import { Drawer } from '../../components/ui/Drawer';
import { formatDate, formatCurrency, formatEventType, clientFullName } from '../../utils/format';
import { useIsMobile } from '../../hooks/useIsMobile';

const EVENT_TYPE_COLORS: Record<string, string> = {
  MATRIMONIO:  'var(--color-primary)',
  BATTESIMO:   'var(--color-gold)',
  COMUNIONE:   '#bb653b',
  CRESIMA:     '#227f8b',
  COMPLEANNO:  'var(--chart-violet)',
  RITRATTO:    'var(--color-success)',
  ALTRO:       '#797876',
};

export default function CalendarPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const calRef = useRef<FullCalendar>(null);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const autoSwitchedRef = useRef(false);
  const isMobile = useIsMobile();

  const { data: events = [] } = useQuery<Event[]>({
    queryKey: ['events-calendar'],
    queryFn: async () => {
      const res = await apiGet<any>('/events?limit=200&sort=eventDate&order=asc&includeAssigned=true');
      return Array.isArray(res) ? res : (res?.data ?? []);
    },
  });

  const { data: performances = [] } = useQuery<import('../../api/types').Performance[]>({
    queryKey: ['performances-calendar'],
    queryFn: async () => {
      const res = await apiGet<any>('/performances?status=CONFERMATO&limit=200&scope=my');
      return Array.isArray(res) ? res : (res?.data ?? []);
    },
  });

  // Auto-switch to timeGridWeek when any event has a startTime (once per mount)
  useEffect(() => {
    if (!autoSwitchedRef.current && events.length > 0 && events.some(e => e.startTime)) {
      autoSwitchedRef.current = true;
      calRef.current?.getApi().changeView('timeGridWeek');
    }
  }, [events]);

  const updateDateMutation = useMutation({
    mutationFn: ({ id, eventDate }: { id: string; eventDate: string }) =>
      apiPatch<Event>(`/events/${id}`, { eventDate }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['events-calendar'] });
      toast.success('Data aggiornata');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const calendarEvents = events.map(ev => {
    const dateOnly = ev.eventDate.split('T')[0];
    const start = ev.startTime ? `${dateOnly}T${ev.startTime}:00` : dateOnly;
    return {
      id: ev.id,
      title: ev.title,
      start,
      allDay: !ev.startTime,
      backgroundColor: EVENT_TYPE_COLORS[ev.eventType] ?? 'var(--color-primary)',
      borderColor: EVENT_TYPE_COLORS[ev.eventType] ?? 'var(--color-primary)',
      extendedProps: { event: ev },
    };
  });

  const performanceCalendarEvents = performances.map(p => {
    const dateOnly = p.eventDate.split('T')[0];
    return {
      id: `perf-${p.id}`,
      title: p.title,
      start: dateOnly,
      allDay: true,
      backgroundColor: 'var(--color-warning)',
      borderColor: 'var(--color-warning)',
      textColor: '#fff',
      classNames: ['fc-event-performance'],
      extendedProps: { performance: p, isPerformance: true },
    };
  });

  const allCalendarEvents = [...calendarEvents, ...performanceCalendarEvents];


  return (
    <div className="page-enter" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <PageHeader
        title="Calendario"
        subtitle={`${events.length} eventi`}
        actions={[
          { label: 'Impostazioni', icon: <Settings size={14} />, onClick: () => navigate('/settings?tab=calendario') },
          { label: 'Aggiorna', icon: <RefreshCw size={14} />, onClick: () => { qc.invalidateQueries({ queryKey: ['events-calendar'] }); qc.invalidateQueries({ queryKey: ['performances-calendar'] }); } },
        ]}
      />

      {/* Legend */}
      <div className="fc-legend">
        {Object.entries(EVENT_TYPE_COLORS).map(([type, color]) => (
          <div key={type} className="fc-legend-item">
            <div className="fc-legend-dot" style={{ background: color }} />
            <span>{formatEventType(type)}</span>
          </div>
        ))}
      </div>

      {/* Calendar */}
      <div style={{
        background: 'var(--glass-bg-card)',
        border: '1px solid rgba(255,255,255,0.10)',
        borderRadius: 'var(--radius-card)',
        padding: '1rem',
        overflow: 'hidden',
      }}>
        <FullCalendar
          ref={calRef}
          plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          locale="it"
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,listMonth',
          }}
          buttonText={{ today: 'Oggi', month: 'Mese', week: 'Settimana', list: 'Lista' }}
          events={allCalendarEvents}
          height={isMobile ? 'auto' : 650}
          editable
          eventClick={info => {
            if (info.event.extendedProps.isPerformance) {
              const perf = info.event.extendedProps.performance;
              if (perf?.id) navigate(`/performances`);
              return;
            }
            const ev = info.event.extendedProps.event as Event;
            setSelectedEvent(ev);
          }}
          eventDrop={info => {
            if (info.event.extendedProps.isPerformance) {
              info.revert();
              return;
            }
            const ev = info.event.extendedProps.event as Event;
            const dropped = info.event.start!;
            // Keep original time if timed event, otherwise use midnight UTC
            const newDate = ev.startTime
              ? new Date(Date.UTC(dropped.getFullYear(), dropped.getMonth(), dropped.getDate())).toISOString()
              : dropped.toISOString();
            updateDateMutation.mutate({ id: ev.id, eventDate: newDate });
          }}
          eventContent={renderEventContent}
          nowIndicator
        />
      </div>

      {/* Event Drawer */}
      <Drawer
        open={!!selectedEvent}
        onClose={() => setSelectedEvent(null)}
        title={selectedEvent?.title}
        footer={
          <Button
            onClick={() => { if (selectedEvent) { navigate(`/events/${selectedEvent.id}`); setSelectedEvent(null); } }}
          >
            Apri evento
          </Button>
        }
      >
        {selectedEvent && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <EventStatusBadge status={selectedEvent.status} />
              <span style={{
                fontSize: 'var(--text-xs)', color: '#FFFFFF',
                background: '#6B7280', padding: '4px 10px',
                borderRadius: 'var(--radius-interactive)',
              }}>
                {formatEventType(selectedEvent.eventType)}
              </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <InfoItem
                label="Data"
                value={`${formatDate(selectedEvent.eventDate, 'EEEE d MMMM yyyy')}${selectedEvent.startTime ? ` — ${selectedEvent.startTime}` : ''}`}
              />
              {selectedEvent.location && <InfoItem label="Location" value={selectedEvent.location} />}
              {selectedEvent.client && (
                <InfoItem label="Cliente" value={clientFullName(selectedEvent.client)} />
              )}
              <InfoItem label="Importo" value={formatCurrency(selectedEvent.totalAmount)} />
            </div>

            {selectedEvent.notes && (
              <div style={{
                background: 'var(--glass-bg-card)',
                borderRadius: 'var(--radius-card)',
                padding: '0.75rem',
              }}>
                <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginBottom: '0.25rem', fontWeight: 600 }}>Note</p>
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                  {selectedEvent.notes.replace(/^#+\s/gm, '')}
                </p>
              </div>
            )}

            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.375rem',
              color: 'var(--color-text-faint)', fontSize: 'var(--text-xs)',
            }}>
              <CalendarCheck size={12} />
              {selectedEvent.googleCalendarId ? 'Sincronizzato con Google Calendar' : 'Non sincronizzato con Google Calendar'}
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}

function renderEventContent(info: { event: { title: string; allDay: boolean; extendedProps: { event?: Event; isPerformance?: boolean } } }) {
  const isPerf = info.event.extendedProps.isPerformance;
  const startTime = info.event.extendedProps.event?.startTime;
  return (
    <div style={{ padding: '0 4px', overflow: 'hidden', fontSize: '11px', fontWeight: 500, display: 'flex', gap: '3px', alignItems: 'center' }}>
      {isPerf && <Target size={10} style={{ flexShrink: 0 }} />}
      {startTime && <span style={{ opacity: 0.8, fontWeight: 400 }}>{startTime}</span>}
      <span>{info.event.title}</span>
    </div>
  );
}

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', gap: '0.5rem' }}>
      <span style={{ width: 80, flexShrink: 0, fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>{label}</span>
      <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)', fontWeight: 500 }}>{value}</span>
    </div>
  );
}
